// src/pages/AdminPortal.jsx

import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, onSnapshot, doc, getDoc, deleteDoc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import AddUser from "./AddUser";
import EditUserModal from "./EditUserModal";
import Attendance from "./Attendance";
import Leave from "./Leave";
import Tasks from "./Tasks";
import ChatRoom from "./ChatRoom";
import Performance from "./Performance";
import Salary from "./Salary";

import EmployeeProfile from "./EmployeeProfile"; 
import AppraisalModal from "../components/AppraisalModal";
import { FaPlus, FaUsers, FaUserShield, FaUserTie, FaEdit, FaTrash, FaTimes, FaSearch, FaTrophy, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import "./AdminPortal.css";
import { useRef } from "react";
import useTabCounts from "../hooks/useTabCounts";

const AdminPortal = () => {
  const { userData } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeManagementTab, setActiveManagementTab] = useState('profile'); 
  const selectedUserCounts = useTabCounts(selectedUser?.uid || selectedUser?.id);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [appraisalUser, setAppraisalUser] = useState(null);
  const [userFilter, setUserFilter] = useState('all'); 
  const [searchQuery, setSearchQuery] = useState(""); // --- (1) ADDED SEARCH STATE ---
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!userData) return;
    setLoading(true);
    const usersRef = collection(db, "users");
    const q = userData.role === 'superadmin'
      ? query(usersRef, where("role", "in", ["admin", "employee"]))
      : query(usersRef, where("role", "==", "employee"));
    const unsub = onSnapshot(q, (snap) => {
      const fetchedUsers = snap.docs.map(d => ({ id: d.id, uid: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name));
      setUsers(fetchedUsers);
      setSelectedUser(prev => {
        if (prev) {
          const updated = fetchedUsers.find(u => u.id === prev.id);
          return updated || fetchedUsers[0] || null;
        }
        return fetchedUsers[0] || null;
      });
      setLoading(false);
    }, (err) => {
      console.error("Error watching users:", err);
      setLoading(false);
    });
    return () => unsub();
  }, [userData]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // ── Take over dashboard-content scroll/padding while this page is active ──
  useEffect(() => {
    const el = document.querySelector('.dashboard-content');
    if (!el) return;
    const prevOverflowY = el.style.overflowY;
    const prevPadding   = el.style.padding;
    el.style.overflowY = 'auto';
    el.style.padding   = '0';
    return () => {
      el.style.overflowY = prevOverflowY;
      el.style.padding   = prevPadding;
    };
  }, []);

  const handleDeleteUser = async (userToDelete) => {
    if (!window.confirm(`Are you sure you want to delete ${userToDelete.name}? This will finalize their experience record and mark them as RESIGNED in Employee List.`)) return;

    try {
      const today = new Date().toISOString().substring(0, 10);
      const experienceDocRef = doc(db, "experience_records", userToDelete.employeeId);
      
      // 1. Check if experience record exists, create if missing (fallback for old employees)
      const experienceDocSnap = await getDoc(experienceDocRef);
      
      if (experienceDocSnap.exists()) {
        // Record exists - update it
        await updateDoc(experienceDocRef, {
          leavingDate: today,
          reasonForLeaving: "Terminated",
        });
        console.log("✅ Experience record updated");
      } else {
        // Record doesn't exist - create it (fallback for old employees)
        await setDoc(experienceDocRef, {
          employeeId: userToDelete.employeeId,
          employeeName: userToDelete.name,
          joiningDate: userToDelete.joiningDate || today,
          position: userToDelete.position || "",
          department: userToDelete.department || "",
          leavingDate: today,
          reasonForLeaving: "Terminated",
          createdAt: serverTimestamp(),
          createdBy: userData.uid,
        });
        console.log("✅ Experience record created (fallback for old employee)");
      }

      // 2. ✨ Update/Create employee_records_complete with resigned status
      const employeeRecordRef = doc(db, "employee_records_complete", userToDelete.employeeId);
      const employeeRecordSnap = await getDoc(employeeRecordRef);
      
      if (employeeRecordSnap.exists()) {
        // Record exists - update it
        await updateDoc(employeeRecordRef, {
          status: "resigned",
          leavingDate: today,
          hasExperienceLetter: true,
          experienceRecordId: userToDelete.employeeId,
          updatedAt: serverTimestamp()
        });
        console.log("✅ Employee record updated to resigned");
      } else {
        // Record doesn't exist - create it (fallback for old employees)
        await setDoc(employeeRecordRef, {
          employeeId: userToDelete.employeeId,
          name: userToDelete.name,
          email: userToDelete.email || "",
          phone: userToDelete.phone || userToDelete.mobile || "",
          department: userToDelete.department || "",
          position: userToDelete.position || "",
          role: userToDelete.role || "employee",
          photoURL: userToDelete.photoURL || "",
          joiningDate: userToDelete.joiningDate || today,
          leavingDate: today,
          status: "resigned",
          uid: userToDelete.id,
          
          // Bank & Statutory (empty for old records)
          location: userToDelete.location || "",
          bankName: userToDelete.bankName || "",
          bankAccountNumber: userToDelete.bankAccountNumber || "",
          ifscCode: userToDelete.ifscCode || "",
          panNumber: userToDelete.panNumber || "",
          pfNumber: userToDelete.pfNumber || "",
          uanNumber: userToDelete.uanNumber || "",
          grossMonthlySalary: userToDelete.grossMonthlySalary || "",
          
          // Salary History
          salaryHistory: {},
          lastSalaryUpdateMonth: null,
          lastSalaryUpdateDate: null,
          
          // Experience Letter flags
          hasExperienceLetter: true,
          experienceRecordId: userToDelete.employeeId,
          
          // Empty arrays
          documents: [],
          driveLinks: [],
          notes: [],
          
          // Metadata
          createdAt: serverTimestamp(),
          createdBy: userData.uid,
          updatedAt: serverTimestamp(),
          syncedAt: serverTimestamp()
        });
        console.log("✅ Employee record created as resigned (fallback for old employee)");
      }

      // 3. Delete from users collection (removes portal access)
      await deleteDoc(doc(db, "users", userToDelete.id));
      
      alert(`User ${userToDelete.name} deleted.\n✅ Marked as RESIGNED in Employee List\n✅ Experience record finalized`);
      
      if (selectedUser?.id === userToDelete.id) {
        const currentFilteredUsers = users.filter(user => {
            if (user.id === userToDelete.id) return false; 
            if (userFilter === 'all' || userData.role !== 'superadmin') return true;
            return user.role === userFilter;
        });
        setSelectedUser(currentFilteredUsers.length > 0 ? currentFilteredUsers[0] : null);
      }
    } catch (err) {
      console.error("Delete user error:", err)
      alert("Failed to delete user. Check the console for details.");
    }
  };

  // --- (2) UPDATED FILTER LOGIC ---
  const filteredUsers = users.filter(user => {
    // Role filter
    const roleMatch = (userFilter === 'all' || userData.role !== 'superadmin') || (user.role === userFilter);
    if (!roleMatch) return false;

    // Search filter
    if (searchQuery.trim() === "") return true; // No search query, pass through

    const lowerSearch = searchQuery.toLowerCase();
    const nameMatch = user.name.toLowerCase().includes(lowerSearch);
    const idMatch = user.employeeId.toLowerCase().includes(lowerSearch);

    return nameMatch || idMatch;
  });


  return (
    <div className="admin-dashboard-layout">
      {/* ── TOP CONTROL BAR ── */}
      <div className="admin-top-bar">
        <div className="admin-top-bar-left">
          <div className="admin-top-bar-icon-wrap">
            <FaUsers className="admin-top-bar-icon" />
          </div>
          <div className="admin-top-bar-text">
            <span className="admin-top-bar-title">Manage Users</span>
            <span className="admin-top-bar-subtitle">Employee directory &amp; access control</span>
          </div>
          {!loading && (
            <span className="admin-top-bar-count">{filteredUsers.length}</span>
          )}
        </div>

        <div className="admin-top-bar-controls">
          {/* User Selector Dropdown */}
          <div className="user-selector-wrap" ref={dropdownRef}>
            <button
              className="user-selector-trigger"
              onClick={() => setDropdownOpen(o => !o)}
            >
              <span className="user-selector-trigger-name">
                {selectedUser ? selectedUser.name : (loading ? 'Loading…' : 'Select a user')}
              </span>
              {selectedUser && (
                <span className={`role-badge-trigger ${selectedUser.role}`}>
                  {selectedUser.employeeId}
                </span>
              )}
              {dropdownOpen ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
            </button>

            {dropdownOpen && (
              <div className="user-selector-menu">
                <div className="user-selector-search">
                  <FaSearch className="search-icon-inner" />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Search name or ID…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Role filter pills — inside dropdown */}
                {userData.role === 'superadmin' && (
                  <div className="user-selector-role-pills">
                    {[['all','All'],['admin','Admins'],['employee','Employees']].map(([val, label]) => (
                      <button
                        key={val}
                        className={`user-selector-role-pill${userFilter === val ? ' active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setUserFilter(val); }}
                      >{label}</button>
                    ))}
                  </div>
                )}

                <ul className="user-selector-list">
                  {loading ? (
                    <li className="user-selector-empty">Loading…</li>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map(user => (
                      <li
                        key={user.id}
                        className={selectedUser?.id === user.id ? 'active' : ''}
                        onClick={() => { setSelectedUser(user); setDropdownOpen(false); setSearchQuery(''); }}
                      >
                        <div className="user-selector-item-info">
                          <span className="user-selector-item-name">{user.name}</span>
                          <span className={`role-badge-small ${user.role}`}>{user.employeeId} · {user.role}</span>
                        </div>
                        <div className="user-selector-item-actions">
                          <button title="Edit" onClick={(e) => { e.stopPropagation(); setEditingUser(user); setDropdownOpen(false); }}><FaEdit /></button>
                          <button title="Delete" onClick={(e) => { e.stopPropagation(); handleDeleteUser(user); }}><FaTrash /></button>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="user-selector-empty">No users found.</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Add User */}
          <button className="add-user-btn-top" onClick={() => setIsAddModalOpen(true)} title="Add New User">
            <FaPlus /> <span>Add User</span>
          </button>


        </div>
      </div>

      {/* ... (main content and modals remain exactly the same) ... */}
      <main className="admin-content">
        {!selectedUser ? (
          <div className="placeholder-content"><h2>{filteredUsers.length === 0 && !loading ? 'No users available' : 'Select a user to manage'}</h2></div>
        ) : (
          <div className="management-portal">
            <div className="management-header">
              <div>
                <h2>Managing: {selectedUser.name}</h2>
                <p>{selectedUser.position || 'N/A'}, <strong>{selectedUser.employeeId}</strong> ({selectedUser.email})</p>
              </div>
            </div>
            <div className="management-tabs">
              {['profile', 'attendance', 'leaves', 'tasks', 'salary', 'performance', 'chat'].map(tab => {
                const count = selectedUserCounts[tab] || 0;
                return (
                  <button key={tab} onClick={() => setActiveManagementTab(tab)} className={activeManagementTab === tab ? 'active' : ''}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {count > 0 && <span className="mgmt-tab-badge">{count > 99 ? '99+' : count}</span>}
                  </button>
                );
              })}
            </div>
            <div className="management-tab-content">
              {activeManagementTab === 'profile' && <EmployeeProfile employee={selectedUser} onAppraisalDone={() => {}} />}
              {activeManagementTab === 'attendance' && <Attendance selectedEmployee={selectedUser} />}
              {activeManagementTab === 'leaves' && <Leave selectedEmployee={selectedUser} />}
              {activeManagementTab === 'tasks' && <Tasks selectedEmployee={selectedUser} />}
              {activeManagementTab === 'salary' && <Salary selectedEmployee={selectedUser} />}
              {activeManagementTab === 'performance' && <Performance selectedEmployee={selectedUser} />}
              {activeManagementTab === 'chat' && <ChatRoom selectedEmployee={selectedUser} />}
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      <AnimatePresence>
        {isAddModalOpen && (
          <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="modal-content" initial={{ y: -50 }} animate={{ y: 0 }} exit={{ y: 50 }}>
              <button className="close-btn" onClick={() => setIsAddModalOpen(false)} data-tooltip="Close"><FaTimes /></button>
              <AddUser onUserAdded={() => { setIsAddModalOpen(false); }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onUserUpdated={() => { setEditingUser(null); }} />
      )}
      {appraisalUser && (
        <AppraisalModal
          employee={appraisalUser}
          onClose={() => setAppraisalUser(null)}
          onAppraisalDone={() => { setAppraisalUser(null); }}
        />
      )}
    </div>
  );
};

export default AdminPortal;